// PokerCity Dealer Certification — Question Engine
// Adapts the question banks into a unified format and provides draw functions

import { TDA_RULES_QUESTIONS } from '../data/dealer/questions_tda_rules';
import { DEALING_PROCEDURE_QUESTIONS } from '../data/dealer/questions_dealing_procedure';
import { CHIP_HANDLING_QUESTIONS } from '../data/dealer/questions_chip_handling';
import { PLAYER_DISPUTES_QUESTIONS } from '../data/dealer/questions_player_disputes';
import { PROFESSIONAL_CONDUCT_QUESTIONS } from '../data/dealer/questions_professional_conduct';
import { SCENARIO_QUESTIONS } from '../data/dealer/questions_scenarios';
import { ALL_HOME_GAME_QUESTIONS } from '../data/dealer/questions_home_game';

export type DealerTrack = 'home' | 'professional';
export type DealerTier = 'none' | 'home_certified' | 'pc_certified' | 'pc_senior' | 'pc_master' | 'pc_pro';
export type ScreenerResult = 'not_ready' | 'home_ready' | 'professional_ready';

export interface DealerQuestion {
  id: string;
  track: string;
  category: string;
  difficulty: 'easy' | 'medium' | 'hard';
  question: string;
  options: string[];
  correct: string;
  correctIndex: number;
  explanation: string;
}

export interface TestConfig {
  totalQuestions: number;
  passMark: number;
  maxAttempts: number;
  cooldownDays: number;
  categories: { category: string; count: number }[];
}

export interface TestResult {
  score: number;
  passed: boolean;
  correctCount: number;
  totalCount: number;
  weakCategories: string[];
  completedAt: number;
  attemptNumber: number;
}

export const HOME_TEST_CONFIG: TestConfig = {
  totalQuestions: 15,
  passMark: 0.70,
  maxAttempts: 999,
  cooldownDays: 1,
  categories: [
    { category: 'basic_rules', count: 5 },
    { category: 'pot_management', count: 4 },
    { category: 'common_situations', count: 3 },
    { category: 'etiquette', count: 3 },
  ],
};

export const PROFESSIONAL_TEST_CONFIG: TestConfig = {
  totalQuestions: 30,
  passMark: 0.85,
  maxAttempts: 3,
  cooldownDays: 7,
  categories: [
    { category: 'tda_rules', count: 8 },
    { category: 'dealing_procedure', count: 7 },
    { category: 'chip_handling', count: 5 },
    { category: 'player_disputes', count: 4 },
    { category: 'professional_conduct', count: 3 },
    { category: 'scenarios', count: 3 },
  ],
};

export const SCREENER_CONFIG = {
  totalQuestions: 10,
  easyCount: 2,
  mediumCount: 5,
  hardCount: 3,
  notReadyIfMissEasy: 2,
  homeReadyMinScore: 0.60,
  professionalReadyMinScore: 0.75,
  professionalReadyMinHard: 2,
};

export const TIER_BADGES: Record<DealerTier, { label: string; color: string; icon: string }> = {
  none:           { label: 'Applicant',           color: '#6B7590', icon: '📝' },
  home_certified: { label: 'Home Game Certified', color: '#059669', icon: '🃏' },
  pc_certified:   { label: 'PC Certified Dealer', color: '#0D9488', icon: '🎖' },
  pc_senior:      { label: 'PC Senior Dealer',    color: '#7C3AED', icon: '⭐' },
  pc_master:      { label: 'PC Master Dealer',    color: '#D97706', icon: '👑' },
  pc_pro:         { label: 'PC Verified Pro',     color: '#DC2626', icon: '💎' },
};

export const CATEGORY_NAMES: Record<string, string> = {
  tda_rules:            'TDA Rules & Rulings',
  dealing_procedure:    'Dealing Procedure',
  chip_handling:        'Chip Handling & Pot Management',
  player_disputes:      'Player Disputes & Escalation',
  professional_conduct: 'Professional Conduct',
  scenarios:            'Scenario-Based Rulings',
  basic_rules:          'Basic Rules & Procedure',
  pot_management:       'Pot Management',
  common_situations:    'Common Situations',
  etiquette:            'Etiquette & Conduct',
};

const letterToIndex = (letter: string): number => ({ A:0, B:1, C:2, D:3 }[letter] ?? 0);
const adapt = (q: any): DealerQuestion => ({ ...q, correctIndex: letterToIndex(q.correct) });

export const ALL_PROFESSIONAL_QUESTIONS: DealerQuestion[] = [
  ...TDA_RULES_QUESTIONS,
  ...DEALING_PROCEDURE_QUESTIONS,
  ...CHIP_HANDLING_QUESTIONS,
  ...PLAYER_DISPUTES_QUESTIONS,
  ...PROFESSIONAL_CONDUCT_QUESTIONS,
  ...SCENARIO_QUESTIONS,
].map(adapt);

export const ALL_HOME_QUESTIONS: DealerQuestion[] = ALL_HOME_GAME_QUESTIONS.map(adapt);

const shuffle = <T>(arr: T[]): T[] => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

export const drawScreener = (): DealerQuestion[] => {
  const pool = ALL_PROFESSIONAL_QUESTIONS;
  const easy   = shuffle(pool.filter(q => q.difficulty === 'easy')).slice(0, SCREENER_CONFIG.easyCount);
  const medium = shuffle(pool.filter(q => q.difficulty === 'medium')).slice(0, SCREENER_CONFIG.mediumCount);
  const hard   = shuffle(pool.filter(q => q.difficulty === 'hard')).slice(0, SCREENER_CONFIG.hardCount);
  return shuffle([...easy, ...medium, ...hard]);
};

export const drawHomeTest = (): DealerQuestion[] =>
  shuffle(HOME_TEST_CONFIG.categories.flatMap(({ category, count }) =>
    shuffle(ALL_HOME_QUESTIONS.filter(q => q.category === category)).slice(0, count)
  ));

export const drawProfessionalTest = (): DealerQuestion[] =>
  shuffle(PROFESSIONAL_TEST_CONFIG.categories.flatMap(({ category, count }) =>
    shuffle(ALL_PROFESSIONAL_QUESTIONS.filter(q => q.category === category)).slice(0, count)
  ));

export const scoreScreener = (questions: DealerQuestion[], answers: number[]) => {
  let correct = 0, hardCorrect = 0, easyMissed = 0;
  const categoryMisses: Record<string, number> = {};
  questions.forEach((q, i) => {
    if (answers[i] === q.correctIndex) { correct++; if (q.difficulty === 'hard') hardCorrect++; }
    else { if (q.difficulty === 'easy') easyMissed++; categoryMisses[q.category] = (categoryMisses[q.category] ?? 0) + 1; }
  });
  const score = correct / questions.length;
  const weakCategories = Object.entries(categoryMisses).sort((a,b) => b[1]-a[1]).map(([c]) => c);
  let result: ScreenerResult;
  if (easyMissed >= SCREENER_CONFIG.notReadyIfMissEasy || score < SCREENER_CONFIG.homeReadyMinScore) result = 'not_ready';
  else if (score >= SCREENER_CONFIG.professionalReadyMinScore && hardCorrect >= SCREENER_CONFIG.professionalReadyMinHard) result = 'professional_ready';
  else result = 'home_ready';
  return { result, score, weakCategories, hardCorrect };
};

export const scoreTest = (questions: DealerQuestion[], answers: number[], config: TestConfig, attemptNumber: number): TestResult => {
  let correct = 0;
  const categoryMisses: Record<string, number> = {};
  questions.forEach((q, i) => {
    if (answers[i] === q.correctIndex) correct++;
    else categoryMisses[q.category] = (categoryMisses[q.category] ?? 0) + 1;
  });
  const score = correct / questions.length;
  const weakCategories = Object.entries(categoryMisses).sort((a,b) => b[1]-a[1]).map(([c]) => c);
  return { score, passed: score >= config.passMark, correctCount: correct, totalCount: questions.length, weakCategories, completedAt: Date.now(), attemptNumber };
};
